import { Controller } from "@hotwired/stimulus";

/**
 * Auto-dismisses a toast after a configurable delay, optionally fading out first.
 *
 * @example
 *   <div data-controller="toast"
 *        data-toast-dismiss-after-value="3000"
 *        data-toast-fade-class="tw-opacity-0"
 *        data-toast-fade-duration-value="200">
 *     Saved!
 *   </div>
 */
export default class extends Controller {
  static classes = ["fade"];
  static values = {
    dismissAfter: { type: Number, default: 0 },
    fadeDuration: { type: Number, default: 200 },
  };

  declare dismissAfterValue: number;
  declare fadeDurationValue: number;
  declare fadeClass: string;
  declare hasFadeClass: boolean;
  declare dismissTimeoutId?: ReturnType<typeof setTimeout>;
  declare removeTimeoutId?: ReturnType<typeof setTimeout>;

  /** Starts the auto-dismiss timer when +dismissAfter+ is greater than zero. */
  connect(): void {
    if (this.dismissAfterValue > 0) {
      this.dismissTimeoutId = setTimeout(
        () => this.fadeOut(),
        this.dismissAfterValue
      );
    }
  }

  /** Clears pending timers to prevent updates after the element is removed. */
  disconnect(): void {
    if (this.dismissTimeoutId) {
      clearTimeout(this.dismissTimeoutId);
    }
    if (this.removeTimeoutId) {
      clearTimeout(this.removeTimeoutId);
    }
  }

  /** Removes the toast immediately and clears any pending timers. */
  dismiss(): void {
    if (!this.isConnected) return;

    clearTimeout(this.dismissTimeoutId);
    clearTimeout(this.removeTimeoutId);
    this.element.remove();
  }

  /** Applies the fade class, then removes the element after +fadeDuration+. */
  private fadeOut(): void {
    if (!this.hasFadeClass) {
      this.dismiss();
      return;
    }

    this.element.classList.add(this.fadeClass);
    this.removeTimeoutId = setTimeout(
      () => this.dismiss(),
      this.fadeDurationValue
    );
  }
}
